import { NextRequest } from 'next/server'
import { RedmineClient } from '@/packages/api/src/redmine-client'
import { decryptSessionStringSync } from './crypto/session-crypt'

/**
 * Create a RedmineClient from authentication headers in the request
 * This handles both API key and Basic auth (username/password) authentication
 */
export function getRedmineClient(request: NextRequest): RedmineClient {
    const apiKey = request.headers.get('x-redmine-api-key')
    const authHeader = request.headers.get('authorization')
    const baseUrl = request.headers.get('x-redmine-base-url') || process.env.REDMINE_BASE_URL

    if (!baseUrl) {
        throw new Error('Missing Redmine base URL')
    }

    if (!apiKey && !authHeader) {
        throw new Error('Missing authentication credentials. Please login first.')
    }

    // Handle Basic auth (username/password)
    if (authHeader && authHeader.startsWith('Basic ')) {
        const base64Credentials = authHeader.split(' ')[1]
        const credentials = Buffer.from(base64Credentials, 'base64').toString('ascii')
        const [username, password] = credentials.split(':')

        return new RedmineClient({
            baseUrl,
            username,
            password
        })
    }

    // Handle API key auth
    return new RedmineClient({
        baseUrl,
        apiKey: apiKey || undefined
    })
}

/**
 * Get authentication headers from the current session
 * This is used by client-side code to add proper headers to API requests
 */
export function getAuthHeaders(): HeadersInit {
    if (typeof window === 'undefined') {
        return {}
    }

    try {
        let sessionData = localStorage.getItem('oxigen_auth_session')

        // Fallback: some deployment setups may persist the session in a cookie
        // (for example during SSR handoffs or when an automated process sets a cookie).
        // If localStorage is empty, check document.cookie for the same key.
        if (!sessionData && typeof document !== 'undefined') {
            const match = document.cookie.match(/(?:^|; )oxigen_auth_session=([^;]+)/)
            if (match) {
                try {
                    sessionData = decodeURIComponent(match[1])
                } catch (e) {
                    sessionData = match[1]
                }
            }
        }

        if (!sessionData) {
            return {}
        }

        // Try decrypt first (encrypted value or obfuscated); fall back to JSON parse
        let session: any = null
        session = decryptSessionStringSync(sessionData) || undefined
        if (!session) {
            try {
                session = JSON.parse(sessionData)
            } catch (err) {
                session = undefined
            }
        }

        // Handle Zustand persist wrapper - extract the state
        const actualSession = session?.state?.session || session

        // Check if session is expired
        if (actualSession.expiresAt && Date.now() >= actualSession.expiresAt) {
            console.log('[API Utils] Session expired, clearing storage and triggering logout')
            localStorage.removeItem('oxigen_auth_session')

            // Import and call handleAuthError dynamically to trigger full logout
            if (typeof window !== 'undefined') {
                import('@/lib/api-error-handler').then(({ handleAuthError }) => {
                    handleAuthError({ message: 'Session expired' })
                })
            }

            return {}
        }

        const headers: HeadersInit = {
            'Content-Type': 'application/json'
        }

        // Try credentials first (username/password auth)
        if (actualSession.credentials?.basicAuth) {
            headers['Authorization'] = `Basic ${actualSession.credentials.basicAuth}`
        }
        // Fall back to API key
        else if (actualSession.apiKey) {
            headers['X-Redmine-API-Key'] = actualSession.apiKey
        }
        else {
        }

        // Get Redmine base URL from tenant context (REQUIRED - set by middleware)
        const tenantCookie = document.cookie.split(';').find(c => c.trim().startsWith('tenant-context='))

        if (!tenantCookie) {
            console.error('[API Utils] No tenant context found. Access application via tenant subdomain.')
            return headers
        }

        try {
            const cookieValue = tenantCookie.split('=').slice(1).join('=').trim()
            const tenantData = JSON.parse(decodeURIComponent(cookieValue))

            if (!tenantData.redmineBaseUrl) {
                console.error('[API Utils] Tenant context missing Redmine URL')
                return headers
            }

            headers['X-Redmine-Base-URL'] = tenantData.redmineBaseUrl

            console.log('[API Utils] Tenant context loaded:', {
                subdomain: tenantData.subdomain,
                baseDomain: tenantData.baseDomain,
                fullDomain: tenantData.fullDomain,
                redmineUrl: tenantData.redmineBaseUrl
            })
        } catch (err) {
            console.error('[API Utils] Failed to parse tenant context:', err)
        }

        return headers
    } catch (error) {
        return {}
    }
}